import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { MenuRendererProps } from 'react-select';
import DropDownSelectField from '../../../../../shared/components/forms/DropDownSelectField';
import { PortalMenu } from '../../../../../shared/scenes/applicationSettings/components/customAssistantConfigurations/DropdownPortalMenu';
import { color_shades_light, voicify_spark_darker, voicify_spark_darkest, voicify_spark_lightest } from '../../../../../shared/constants/colors';
import { sidebarBodyTypography, sidebarCaptionTypography, sidebarFocusStyles } from './AssistantSidebar.styles';

interface AssistantMoveDropdownProps {
    groups: { id: string; name: string }[];
    disabled: boolean;
    onSelect: (groupId: string) => void;
}

interface MoveMenuRendererProps extends MenuRendererProps {
    instancePrefix: string;
    onOptionRef: (element: HTMLDivElement | null, isFocused: boolean) => void;
}

const AssistantMoveDropdown: React.FC<AssistantMoveDropdownProps> = ({ groups, disabled, onSelect }) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    return <Wrapper ref={anchorRef}>
        <MoveSelect
            aria-label="Move selected assistants to group"
            size="small"
            placeholder="Move to…"
            value={null}
            disabled={disabled}
            clearable={false}
            searchable={false}
            options={[...groups.map(group => ({ value: group.id, label: group.name })), { value: 'new', label: 'New group…' }]}
            onChange={option => { if (option && !disabled) onSelect(String(option.value)); }}
            arrowRenderer={({ isOpen }) => <Arrow viewBox="0 0 16 16" $isOpen={isOpen} aria-hidden="true"><path d="M4 6l4 4 4-4" /></Arrow>}
            menuRenderer={(menu: MoveMenuRendererProps) => <PortalMenu anchorEl={anchorRef.current} openAbove={false} matchWidth={true}>
                <Menu id={`${menu.instancePrefix}-list`} role="listbox" aria-label="Move to group">
                    {menu.options.map((option, index) => <MenuOption
                        key={String(option.value)}
                        id={`${menu.instancePrefix}-option-${index}`}
                        ref={element => {
                            const isFocused = menu.focusedOption === option;
                            menu.onOptionRef(element, isFocused);
                            if (element && isFocused && element.parentElement) {
                                const list = element.parentElement;
                                if (element.offsetTop < list.scrollTop) list.scrollTop = element.offsetTop;
                                else if (element.offsetTop + element.offsetHeight > list.scrollTop + list.clientHeight) {
                                    list.scrollTop = element.offsetTop + element.offsetHeight - list.clientHeight;
                                }
                            }
                        }}
                        role="option"
                        aria-selected={false}
                        $isFocused={menu.focusedOption === option}
                        $isNew={option.value === 'new'}
                        onMouseEnter={() => menu.focusOption(option)}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => { if (!disabled) menu.selectValue(option); }}
                    >
                        {option.value === 'new' && <Plus aria-hidden="true">+</Plus>}
                        <span>{option.label}</span>
                    </MenuOption>)}
                </Menu>
            </PortalMenu>}
        />
    </Wrapper>;
};

const Wrapper = styled.div`flex: 1; min-width: 0;`;
const MoveSelect = styled(DropDownSelectField)`
    ${sidebarCaptionTypography}
    width: 100%;
    min-width: 0;

    && .Select-control {
        height: 28px;
        min-height: 28px;
        border: 1px solid ${color_shades_light};
        border-radius: 8px;
        background: white;
        box-shadow: 0 1px 2px rgba(48, 69, 98, 0.04);
        font: inherit;
        transition: border-color 0.15s, box-shadow 0.15s;
    }
    && .Select-placeholder {
        padding: 0 8px;
        color: ${voicify_spark_darker};
        font: inherit;
        line-height: 26px;
    }
    && .Select-input { height: 26px; padding: 0 8px; }
    && .Select-arrow-zone { width: 24px; padding-right: 8px; }
    &&:not(.is-disabled) .Select-control:hover { border-color: ${voicify_spark_darker}; }
    &&.is-open .Select-control, &&.is-focused:not(.is-open) .Select-control {
        border-radius: 8px;
        border-color: ${voicify_spark_darker};
        box-shadow: 0 0 0 2px ${voicify_spark_lightest};
    }
    && .Select-menu-outer { display: none; }
    &&.is-disabled { opacity: 0.5; }
`;
const Arrow = styled.svg<{ $isOpen: boolean }>`
    width: 12px;
    height: 12px;
    fill: none;
    stroke: ${voicify_spark_darker};
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'none'};
    transition: transform 0.15s;
`;
const Menu = styled.div`
    ${sidebarBodyTypography}
    font-family: Muli, sans-serif;
    position: relative;
    box-sizing: border-box;
    margin-top: 6px;
    padding: 4px;
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    background: white;
    box-shadow: 0 6px 16px rgba(48, 69, 98, 0.12);
`;
const MenuOption = styled.div<{ $isFocused: boolean; $isNew: boolean }>`
    ${sidebarFocusStyles}
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px;
    border-radius: 5px;
    color: ${props => props.$isFocused || props.$isNew ? voicify_spark_darker : voicify_spark_darkest};
    background: ${props => props.$isFocused ? voicify_spark_lightest : 'transparent'};
    cursor: pointer;
    overflow-wrap: anywhere;
    transition: background-color 0.15s, color 0.15s;
    ${props => props.$isNew && `margin-top: 4px; border-top: 1px solid ${color_shades_light}; border-top-left-radius: 0; border-top-right-radius: 0; font-weight: 600;`}

    &:hover { background: ${voicify_spark_lightest}; color: ${voicify_spark_darker}; }
`;
const Plus = styled.span`font-size: 15px; line-height: 1; flex-shrink: 0;`;

export default AssistantMoveDropdown;
